import type * as React from "react";
import { FileText, Receipt } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ActionFieldValue } from "../ActionFieldEditor";
import {
  VendorPreviewField,
  VendorPreviewShell,
} from "./VendorPreviewShell";

/**
 * QuotationDraftPreview — HITL card for ``generate_quotation``
 * proposals (issue #903, July 2026).
 *
 * The BE ships ``QuotationDraftPreview`` (see
 * ``src/models/action_models.py``) with:
 *
 *   - ``content_item_id`` / ``filename`` — the CAD drawing being quoted
 *   - ``matched_count`` / ``unmatched_count`` — components with/without
 *     a resolved price from the tenant's price sources
 *   - ``estimated_total`` / ``currency`` — the running total (null when
 *     every component was flagged manual-only)
 *   - ``change_lines: string[]`` — reviewer-readable per-BOM-line
 *     summary
 *
 * All arrive flattened onto ``proposedFields`` via
 * :func:`src.services.action_proposal_flatten` — read-only in the card,
 * the user only confirms/rejects (no editable amount by design: the
 * numbers reflect what actually matched, editing them would defeat the
 * "no invented prices" invariant of the pipeline).
 *
 * Every user-facing string is Lingui-macro'd so the copy travels
 * through the catalog. Currency formatting uses ``Intl.NumberFormat``
 * scoped to the active ``i18n.locale`` so a French tenant sees "12,50 €"
 * while an Italian one sees "€ 12,50".
 */

export interface QuotationDraftPreviewProps {
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
}

function toNumber(v: ActionFieldValue | undefined): number | null {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function toString(v: ActionFieldValue | undefined): string | null {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return null;
}

function formatCurrency(amount: number, currency: string, locale: string): string {
  const cur = currency.trim().toUpperCase() || "EUR";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${cur}`;
  }
}

/** Parse a change-line string emitted by the BE. Format is:
 *   ``{glyph} {component} ×{qty} → {price} ({desc}) [{source_label}]``
 *
 * Splits into structured parts so the chip can render the source
 * label as a distinct pill (per-line HITL affordance — the reviewer
 * spots at a glance whether a price came from SAP / manual listino /
 * DB and can decide to reject that line). Glyph + source are BE-side
 * emissions and are locale-neutral. */
function parseChangeLine(line: string): {
  glyph: "check" | "warn" | null;
  body: string;
  sourceLabel: string | null;
} {
  const trimmed = line.trimStart();
  let glyph: "check" | "warn" | null = null;
  let rest = trimmed;
  if (trimmed.startsWith("✓")) {
    glyph = "check";
    rest = trimmed.slice(1).trimStart();
  } else if (trimmed.startsWith("⚠")) {
    glyph = "warn";
    rest = trimmed.slice(1).trimStart();
  }
  // Trailing ``[Source Label]`` — greedy match to survive nested
  // brackets inside the description. Only strip the outermost pair
  // at the very END of the string so a description containing "[X]"
  // doesn't get truncated.
  const sourceMatch = rest.match(/\s*\[([^\]]+)\]\s*$/);
  if (sourceMatch) {
    return {
      glyph,
      body: rest.slice(0, sourceMatch.index).trimEnd(),
      sourceLabel: sourceMatch[1]!.trim(),
    };
  }
  return { glyph, body: rest, sourceLabel: null };
}

export const QuotationDraftPreview: React.FC<QuotationDraftPreviewProps> = ({
  proposedFields,
  executedFields,
}) => {
  const { i18n } = useLingui();
  const merged = { ...proposedFields, ...(executedFields ?? {}) };

  const filename = toString(merged.filename) ?? i18n._(msg`Disegno CAD`);
  const matched = toNumber(merged.matched_count) ?? 0;
  const unmatched = toNumber(merged.unmatched_count) ?? 0;
  const total = toNumber(merged.estimated_total);
  const currency = toString(merged.currency) ?? "EUR";

  const rawLines = merged.change_lines;
  const lines: readonly string[] = Array.isArray(rawLines)
    ? (rawLines as unknown[]).filter((s): s is string => typeof s === "string")
    : typeof rawLines === "string" && rawLines.trim() !== ""
      ? rawLines
          .split(/\n|(?=[✓⚠])/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const bottomBanner =
    total !== null && matched > 0
      ? {
          label: i18n._(msg`Totale stimato · ${matched} voci`),
          value: (
            <span className="tabular-nums">
              {formatCurrency(total, currency, i18n.locale ?? "it-IT")}
            </span>
          ),
          tone: "accent" as const,
        }
      : {
          label: i18n._(msg`Nessuna voce prezzata`),
          value: (
            <span className="tabular-nums">
              <Trans>Da quotare manualmente</Trans>
            </span>
          ),
          tone: "warning" as const,
        };

  return (
    <VendorPreviewShell
      icon={<Receipt className="h-4 w-4" strokeWidth={1.75} />}
      title={i18n._(msg`Bozza preventivo cliente`)}
      caption={
        <span className="inline-flex items-center gap-1">
          <FileText className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">{filename}</span>
        </span>
      }
      bottomBanner={bottomBanner}
      footnote={i18n._(
        msg`Ogni voce mostra la fonte del prezzo — Manuale, SAP, Salesforce, HubSpot o Database. Rifiuta la bozza se una fonte non è aggiornata; approvala per salvarla in Preventivi. Nessuna email al cliente viene inviata.`,
      )}
    >
      <VendorPreviewField
        label={i18n._(msg`Componenti prezzati`)}
        value={
          <span className="font-semibold text-emerald-700 tabular-nums">
            {matched}
          </span>
        }
      />
      <VendorPreviewField
        label={i18n._(msg`Da quotare manualmente`)}
        value={
          <span
            className={
              unmatched > 0
                ? "font-semibold text-amber-800 tabular-nums"
                : "text-ink-2 tabular-nums"
            }
          >
            {unmatched}
          </span>
        }
      />
      {lines.length > 0 && (
        <VendorPreviewField
          label={i18n._(msg`Dettaglio distinta`)}
          wide
          value={
            <ul className="mt-1 flex max-h-56 flex-col gap-1 overflow-y-auto pr-1">
              {lines.map((line, idx) => {
                const { glyph, body, sourceLabel } = parseChangeLine(line);
                return (
                  <li
                    key={`${idx}-${line.slice(0, 20)}`}
                    className="flex items-start gap-1.5 text-[12px] leading-snug"
                  >
                    {glyph === "check" && (
                      <span
                        aria-hidden
                        className="mt-[3px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                      />
                    )}
                    {glyph === "warn" && (
                      <span
                        aria-hidden
                        className="mt-[3px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                      />
                    )}
                    {glyph === null && (
                      <span
                        aria-hidden
                        className="mt-[3px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ink-3/50"
                      />
                    )}
                    <span className="flex-1 flex-wrap items-baseline gap-1 tabular-nums text-ink-1">
                      <span>{body}</span>
                      {sourceLabel && (
                        <span
                          className="ml-1 inline-flex items-center rounded-full border border-hairline bg-white/60 px-1.5 py-0.5 font-medium text-[10px] text-ink-2 uppercase tracking-wide"
                          title={i18n._(msg`Fonte del prezzo`)}
                        >
                          {sourceLabel}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          }
        />
      )}
    </VendorPreviewShell>
  );
};
QuotationDraftPreview.displayName = "QuotationDraftPreview";
